/**
 * UnifiedChartContainer - Contains all 4 synchronized charts
 */

import { useEffect, useState, useCallback } from 'react';
import useChartData from '../../hooks/useChartData.js';
import PriceChart from './PriceChart.jsx';
import MACDChart from './MACDChart.jsx';
import LROChart from './LROChart.jsx';
import WaveTrendChart from './WaveTrendChart.jsx';
import './UnifiedChartContainer.css';

export default function UnifiedChartContainer({ ticker }) {
  const { chartData, historicalData, isLoading, error } = useChartData(ticker, '1y');
  const [visibleRange, setVisibleRange] = useState(null);

  // Initialize chart sync with crosshair synchronization
  useEffect(() => {
    window.chartSync = {
      charts: {},
      series: {}, // Store main series for each chart
      crosshairCallbacks: {},
      registerChart(name, chart, mainSeries) {
        this.charts[name] = chart;
        if (mainSeries) {
          this.series[name] = mainSeries;
        }
      },
      registerCrosshairCallback(name, callback) {
        this.crosshairCallbacks[name] = callback;
      },
      unregisterCrosshairCallback(name) {
        delete this.crosshairCallbacks[name];
      },
      syncRange(range) {
        Object.values(this.charts).forEach(chart => {
          chart.timeScale().setVisibleRange(range);
        });
      },
      syncCrosshair(sourceChart, param) {
        // Sync crosshair position across all charts using time coordinate
        Object.entries(this.charts).forEach(([name, chart]) => {
          if (chart !== sourceChart) {
            if (param && param.time && this.series[name]) {
              // Use the series stored for this chart
              chart.setCrosshairPosition(0, param.time, this.series[name]);
            } else if (!param || !param.time) {
              chart.clearCrosshairPosition();
            }
          }
        });
      },
    };

    return () => {
      delete window.chartSync;
    };
  }, []);

  const handleVisibleRangeChange = useCallback((range) => {
    setVisibleRange(range);
    if (window.chartSync) {
      window.chartSync.syncRange(range);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="unified-chart-container loading">
        <div className="loading-spinner">Loading charts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unified-chart-container error">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  if (!historicalData) {
    return (
      <div className="unified-chart-container empty">
        <div className="empty-message">No chart data available</div>
      </div>
    );
  }

  return (
    <div className="unified-chart-container">
      <div className="chart-row">
        <PriceChart
          data={historicalData}
          height={400}
          onVisibleRangeChange={handleVisibleRangeChange}
        />
      </div>

      <div className="chart-row">
        <MACDChart
          data={historicalData}
          height={200}
          onVisibleRangeChange={handleVisibleRangeChange}
        />
      </div>

      <div className="chart-row">
        <LROChart
          data={historicalData}
          height={250}
          onVisibleRangeChange={handleVisibleRangeChange}
        />
      </div>

      <div className="chart-row">
        <WaveTrendChart
          data={historicalData}
          height={250}
          onVisibleRangeChange={handleVisibleRangeChange}
        />
      </div>
    </div>
  );
}
